import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, TrendingUp, Monitor, Image, Users, Package, BarChart3, Calendar, Clock } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Analytics = () => {
  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7'); // 7, 30, 90 days
  const [overviewStats, setOverviewStats] = useState({
    screens: { total: 0, online: 0, offline: 0 },
    media: { total: 0 },
    playlists: { total: 0 },
    widgets: { total: 0 },
    users: { activeLastWeek: 0 },
    content: { viewsLast30Days: 0 }
  });
  const [screenActivity, setScreenActivity] = useState([]);
  const [screenUptime, setScreenUptime] = useState([]);
  const [contentPerformance, setContentPerformance] = useState([]);
  const [widgetUsage, setWidgetUsage] = useState([]);
  const [userActivity, setUserActivity] = useState([]);

  // Access Control Check
  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    const tier = localStorage.getItem('subscriptionTier');
    
    if (userRole !== 'super_admin' && 
        (userRole !== 'admin' || (tier !== 'business' && tier !== 'enterprise'))) {
      window.location.href = '/';
    }
  }, []);

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const loadAnalyticsData = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    // Load all analytics data in parallel
    const [overview, screens, uptime, content, widgets, users] = await Promise.all([
      fetch(`${API_URL}/analytics/overview`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/analytics/screens/activity?days=${timeRange}`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/analytics/screens/uptime?days=${timeRange}`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/analytics/content/performance?days=${timeRange}&limit=10`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/analytics/widgets/usage?days=${timeRange}`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/analytics/users/activity?days=${timeRange}&limit=10`, { headers }).then(r => r.json())
    ]);

      setOverviewStats(overview);
      setScreenActivity(screens);
      setScreenUptime(uptime);
      setContentPerformance(content);
      setWidgetUsage(widgets);
      setUserActivity(users);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format duration in seconds to readable format
  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('analytics.userActivity.justNow');
    if (diffMins < 60) return t('analytics.userActivity.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('analytics.userActivity.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('analytics.userActivity.daysAgo', { count: diffDays });
    
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Prepare chart data
  const prepareUptimeChartData = () => {
    return screenUptime.map(screen => ({
      name: screen.screen_name,
      uptime: parseFloat(screen.uptime_percentage),
      hours: parseFloat((screen.total_uptime_seconds / 3600).toFixed(1))
    }));
  };

  const prepareContentChartData = () => {
    return contentPerformance.slice(0, 5).map((content, index) => ({
      name: content.media_title.substring(0, 20) + '...',
      views: parseInt(content.view_count),
      duration: Math.round(content.avg_duration_seconds)
    }));
  };

  const prepareWidgetPieData = () => {
    return widgetUsage.map(widget => ({
      name: widget.widget_type,
      value: parseInt(widget.total_events)
    }));
  };

  // Colors for charts
  const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-blue-500" />
                {t('analytics.title')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                {t('analytics.subtitle')}
              </p>
            </div>

            {/* Time Range Selector */}
            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setTimeRange('7')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === '7'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {t('analytics.timeRange.7days')}
              </button>
              <button
                onClick={() => setTimeRange('30')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === '30'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {t('analytics.timeRange.30days')}
              </button>
              <button
                onClick={() => setTimeRange('90')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === '90'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {t('analytics.timeRange.90days')}
              </button>
            </div>
          </div>
        </div>

        {/* Overview Stats Cards */}
        {overviewStats && overviewStats.screens && overviewStats.media && overviewStats.playlists && overviewStats.widgets && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Screens */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Monitor className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  {overviewStats.screens.online}/{overviewStats.screens.total} {t('analytics.overview.online')}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {overviewStats.screens.total}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('analytics.overview.totalScreens')}</p>
            </div>

            {/* Total Media */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Image className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {formatNumber(overviewStats.media.total)}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('analytics.overview.mediaItems')}</p>
            </div>

            {/* Total Playlists */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Package className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {overviewStats.playlists.total}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('analytics.overview.playlists')}</p>
            </div>

            {/* Total Widgets */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <Activity className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {overviewStats.widgets.total}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('analytics.overview.activeWidgets')}</p>
            </div>
          </div>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          
          {/* Screen Uptime Chart */}
          {screenUptime.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Monitor className="w-5 h-5 text-blue-500" />
                {t('analytics.charts.screenUptimeComparison')}
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={prepareUptimeChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#f3f4f6' }}
                  />
                  <Legend />
                  <Bar dataKey="uptime" fill="#3b82f6" name={t('analytics.charts.uptimePercent')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Content Performance Chart */}
          {contentPerformance.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-500" />
                {t('analytics.charts.topContentViews')}
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={prepareContentChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9ca3af" angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#f3f4f6' }}
                  />
                  <Legend />
                  <Bar dataKey="views" fill="#8b5cf6" name={t('analytics.charts.views')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

        </div>

        {/* Widget Usage Pie Chart */}
        {widgetUsage.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-500" />
              {t('analytics.charts.widgetUsageDistribution')}
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={prepareWidgetPieData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {prepareWidgetPieData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* User Activity Timeline */}
        {userActivity.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500" />
              {t('analytics.userActivity.title')}
            </h2>
            <div className="space-y-3">
              {userActivity.map((activity, index) => (
                <div key={activity.id} className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {activity.user_email}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                        {activity.action_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>{formatTimestamp(activity.timestamp)}</span>
                      {activity.resource_type && (
                        <span className="text-gray-500 dark:text-gray-500">
                          • {activity.resource_type}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Screen Uptime Table */}
        {screenUptime.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Monitor className="w-5 h-5 text-blue-500" />
              {t('analytics.screenUptime.title')} ({t('analytics.screenUptime.lastDays', { days: timeRange })})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">{t('analytics.screenUptime.screenName')}</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">{t('analytics.screenUptime.uptime')}</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">{t('analytics.screenUptime.totalHours')}</th>
                  </tr>
                </thead>
                <tbody>
                  {screenUptime.map((screen) => (
                    <tr key={screen.screen_id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-medium">
                        {screen.screen_name}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          screen.uptime_percentage >= 95 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                          screen.uptime_percentage >= 80 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {screen.uptime_percentage}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                        {(screen.total_uptime_seconds / 3600).toFixed(1)}h
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Content Performance Table */}
        {contentPerformance.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              {t('analytics.contentPerformance.title')} ({t('analytics.contentPerformance.lastDays', { days: timeRange })})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">{t('analytics.contentPerformance.mediaTitle')}</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">{t('analytics.contentPerformance.type')}</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">{t('analytics.contentPerformance.views')}</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">{t('analytics.contentPerformance.avgDuration')}</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">{t('analytics.contentPerformance.totalTime')}</th>
                  </tr>
                </thead>
                <tbody>
                  {contentPerformance.map((content, index) => (
                    <tr key={content.media_id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-medium">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 dark:text-gray-600">#{index + 1}</span>
                          {content.media_title}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                          {content.media_type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white text-right font-medium">
                        {formatNumber(content.view_count)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                        {formatDuration(Math.round(content.avg_duration_seconds))}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                        {formatDuration(content.total_duration_seconds)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Widget Usage Stats */}
        {widgetUsage.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-500" />
              {t('analytics.widgetUsage.title')} ({t('analytics.widgetUsage.lastDays', { days: timeRange })})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {widgetUsage.map((widget) => (
                <div key={widget.widget_type} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
                      {widget.widget_type}
                    </h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {widget.unique_widgets} {widget.unique_widgets === 1 ? 'Instanz' : 'Instanzen'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{t('analytics.widgetUsage.totalEvents')}</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(widget.total_events)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{t('analytics.widgetUsage.views')}</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(widget.view_count)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{t('analytics.widgetUsage.configurations')}</span>
                      <span className="font-medium text-gray-900 dark:text-white">{widget.config_count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Analytics;